import type { ArtefactSummary } from "@esi/shared";
import {
  ArrowLeft,
  BookCheck,
  BookPlus,
  BookUp,
  ChevronLeft,
  ChevronRight,
  Download,
  X,
} from "lucide-react";
import { type ReactNode, type RefObject, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ApiError } from "../../api-errors.ts";
import { canChange } from "../../projects/capabilities.ts";
import { useProjects } from "../../projects/queries.ts";
import { Link, type OpenArtefact, projectHomePath } from "../../router.tsx";
import { AddToKnowledgeDialog } from "./AddToKnowledgeDialog.tsx";
import { KindIcon } from "./kind.tsx";
import {
  artefactDownloadHref,
  type ConversationScope,
  useArtefacts,
  useArtefactVersion,
  useKnowledgePreview,
} from "./queries.ts";
import { ArtefactBody } from "./renderers.tsx";

export interface ArtefactPanelProps {
  scope: ConversationScope;
  open: OpenArtefact;
  /** Shows another version (replaces the history entry). */
  onVersion: (version: number) => void;
  onClose: () => void;
  /** Back to the knowledge panel (project conversations on wide screens). */
  onBack?: (() => void) | undefined;
  /** Receives focus when the panel opens (the title). */
  headingRef?: RefObject<HTMLHeadingElement | null>;
  /** In the narrow sheet, the sheet supplies the close control. */
  inSheet?: boolean;
}

/**
 * The open artefact beside the conversation (design §1, §5, §6): a bar with the way back and the
 * actions, the title with its kind and the version switcher, a notice when a newer version is
 * written while an older one is read, then the version rendered by kind.
 */
export function ArtefactPanel(props: ArtefactPanelProps) {
  const { scope, open, onVersion, onClose, onBack, headingRef, inSheet } = props;
  const { t } = useTranslation();
  const list = useArtefacts(scope);
  const summary = list.data?.artefacts.find((a) => a.id === open.artefactId);
  const number = open.version ?? summary?.version ?? null;
  const version = useArtefactVersion(scope, open.artefactId, number);
  const missing =
    (version.error instanceof ApiError && version.error.status === 404) ||
    (list.isSuccess && !summary);
  const title = version.data?.title ?? summary?.title ?? t("artefacts.untitled");
  const kind = version.data?.kind ?? summary?.kind;
  const knowledge = useKnowledgeState(scope, open.artefactId, missing ? null : number);
  // The version being added: the dialog outlives its button, which goes once the version is in
  // knowledge (focus then returns to the title).
  const [adding, setAdding] = useState<number | null>(null);
  const addButton = useRef<HTMLButtonElement>(null);
  // The way the switcher last moved: a version the viewer may not read is skipped that way.
  // Null for a version asked for directly (a link, a card): that one says it is unavailable.
  const direction = useRef<1 | -1 | null>(null);
  const go = (next: number) => {
    direction.current = number !== null && next < number ? -1 : 1;
    onVersion(next);
  };

  // Opened at "the latest" (no `v`): pinned to the version it resolved to, so a revision written
  // meanwhile is offered as newer instead of replacing what is being read (design §5).
  const pinned = open.version === null ? (summary?.version ?? null) : null;
  useEffect(() => {
    if (pinned !== null) {
      onVersion(pinned);
    }
  }, [pinned, onVersion]);

  // A version shown: the next unreadable one is skipped only if the switcher asks for it.
  useEffect(() => {
    if (version.data) {
      direction.current = null;
    }
  }, [version.data]);

  // A version the viewer may not read, reached from the switcher, is skipped (design §5).
  useEffect(() => {
    if (
      direction.current !== null &&
      version.error instanceof ApiError &&
      version.error.status === 404 &&
      summary &&
      number !== null &&
      number < summary.version
    ) {
      const next = number + direction.current;
      onVersion(next >= 1 ? next : summary.version);
    }
  }, [version.error, summary, number, onVersion]);

  return (
    <div data-esi-content="documents" className="flex min-h-0 flex-1 flex-col">
      {inSheet ? null : (
        <div className="flex h-bar shrink-0 items-center justify-between gap-2 border-b border-line px-2">
          {onBack ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              aria-label={t("artefacts.panel.backLabel")}
            >
              <ArrowLeft aria-hidden="true" strokeWidth={1.75} />
              {t("artefacts.panel.back")}
            </Button>
          ) : (
            <span />
          )}
          <IconButton label={t("artefacts.panel.close")} onClick={onClose}>
            <X aria-hidden="true" strokeWidth={1.75} />
          </IconButton>
        </div>
      )}
      <div className="flex shrink-0 flex-col gap-2 border-b border-line px-4 py-3">
        <div className="flex items-start gap-2.5">
          {kind ? (
            <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md bg-surface-sunken">
              <KindIcon kind={kind} className="text-fg-secondary" />
            </span>
          ) : null}
          <div className="min-w-0 flex-1">
            <h2
              ref={headingRef}
              tabIndex={-1}
              className="text-[15px] font-semibold tracking-tight break-words text-fg outline-none"
            >
              {title}
            </h2>
            {kind ? (
              <p className="text-xs text-fg-muted">
                {t(`artefacts.kind.${kind}`)}
                {version.data?.language ? ` · ${version.data.language}` : ""}
              </p>
            ) : null}
          </div>
          {number !== null && !missing && knowledge.action ? (
            <KnowledgeButton
              ref={addButton}
              action={knowledge.action}
              version={number}
              title={title}
              onClick={() => setAdding(number)}
            />
          ) : null}
          {number !== null && !missing ? (
            <Button variant="outline" size="sm" asChild>
              <a
                href={artefactDownloadHref(scope, open.artefactId, number, kind ?? undefined)}
                download
                aria-label={t("artefacts.panel.downloadNamed", { version: number, title })}
              >
                <Download aria-hidden="true" strokeWidth={1.75} />
                <span className="max-sm:sr-only">{t("artefacts.panel.download")}</span>
              </a>
            </Button>
          ) : null}
        </div>
        {summary && number !== null && !(list.isSuccess && !summary) ? (
          <VersionSwitcher summary={summary} number={number} onVersion={go} />
        ) : null}
        {knowledge.inKnowledge ? (
          <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-fg-muted tabular-nums">
            <BookCheck aria-hidden="true" className="size-3.5 shrink-0" strokeWidth={1.75} />
            {t("artefactKnowledge.inKnowledge", { version: knowledge.inKnowledge.version })}
            <span aria-hidden="true">·</span>
            <Link
              href={knowledge.inKnowledge.href}
              className="font-medium text-accent-text underline-offset-4 hover:underline"
            >
              {t("artefactKnowledge.showInFiles")}
            </Link>
          </p>
        ) : null}
      </div>
      {adding !== null ? (
        <AddToKnowledgeDialog
          open
          onClose={() => setAdding(null)}
          scope={scope}
          artefactId={open.artefactId}
          version={adding}
          title={title}
          returnFocus={() =>
            addButton.current?.isConnected ? addButton.current : (headingRef?.current ?? null)
          }
        />
      ) : null}
      {summary && number !== null && number < summary.version && !(list.isSuccess && !summary) ? (
        <div className="shrink-0 border-b border-line px-4 py-2" role="status">
          <p className="flex flex-wrap items-baseline gap-x-2 text-sm text-fg-secondary">
            {t("artefacts.versions.newer")}
            <button
              type="button"
              onClick={() => go(summary.version)}
              className="font-medium text-accent-text underline-offset-4 hover:underline"
            >
              {t("artefacts.versions.showNewer", { version: summary.version })}
            </button>
          </p>
        </div>
      ) : null}
      <div
        // biome-ignore lint/a11y/noNoninteractiveTabindex: a scroll container must be focusable
        tabIndex={0}
        className="min-h-0 flex-1 overflow-y-auto px-4 py-4 outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-inset"
      >
        {missing ? (
          <Notice tone="neutral">{t("artefacts.panel.notFound")}</Notice>
        ) : version.isError || list.isError ? (
          <Notice
            tone="danger"
            action={
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  void list.refetch();
                  void version.refetch();
                }}
              >
                {t("artefacts.panel.retry")}
              </Button>
            }
          >
            {t("artefacts.panel.failed")}
          </Notice>
        ) : version.data ? (
          <ArtefactBody version={version.data} scope={scope} />
        ) : (
          <div className="flex flex-col gap-2" aria-busy="true">
            <span className="sr-only">{t("artefacts.panel.loading")}</span>
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * "Version 2 of 3" with previous and next, and "Latest" when not on it. Every button is named
 * with the version it opens. With versions the viewer may not read, only the number is shown.
 */
function VersionSwitcher(props: {
  summary: ArtefactSummary;
  number: number;
  onVersion: (version: number) => void;
}) {
  const { summary, number, onVersion } = props;
  const { t } = useTranslation();
  if (summary.version === 1) {
    return null;
  }
  const exact = summary.versions === summary.version;
  return (
    <nav aria-label={t("artefacts.versions.label")} className="flex items-center gap-1 text-xs">
      <IconButton
        label={t("artefacts.versions.previous", { version: number - 1 })}
        // Only one readable version: nothing to step to.
        disabled={number <= 1 || summary.versions <= 1}
        onClick={() => onVersion(number - 1)}
        small
      >
        <ChevronLeft aria-hidden="true" strokeWidth={1.75} />
      </IconButton>
      <span className="min-w-[7.5rem] text-center font-medium text-fg-secondary tabular-nums">
        {exact
          ? t("artefacts.versions.position", { version: number, count: summary.version })
          : t("artefacts.version", { version: number })}
      </span>
      <IconButton
        label={t("artefacts.versions.next", { version: number + 1 })}
        disabled={number >= summary.version || summary.versions <= 1}
        onClick={() => onVersion(number + 1)}
        small
      >
        <ChevronRight aria-hidden="true" strokeWidth={1.75} />
      </IconButton>
      {number < summary.version ? (
        <Button
          variant="ghost"
          size="xs"
          className="ml-1"
          aria-label={t("artefacts.versions.latestNamed", { version: summary.version })}
          onClick={() => onVersion(summary.version)}
        >
          {t("artefacts.versions.latest")}
        </Button>
      ) : null}
    </nav>
  );
}

export function IconButton(props: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  small?: boolean;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size={props.small ? "icon-xs" : "icon-sm"}
          aria-label={props.label}
          disabled={props.disabled}
          onClick={props.onClick}
        >
          {props.children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{props.label}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Whether the viewer may add the shown version to knowledge, and whether it (or an earlier one)
 * is there (artefacts-to-knowledge, design §9): a project conversation asks its project; a
 * standalone one offers the action when some project accepts the viewer's uploads, and the
 * dialog picks it. The server decides again on confirming.
 */
function useKnowledgeState(scope: ConversationScope, artefactId: string, version: number | null) {
  const projects = useProjects();
  const project = scope.projectId ? projects.data?.find((p) => p.id === scope.projectId) : null;
  const mayUpload = scope.projectId
    ? project !== null && project !== undefined && canChange(project, "file.upload")
    : (projects.data ?? []).some((p) => canChange(p, "file.upload"));
  const preview = useKnowledgePreview(scope, artefactId, version, {
    target: null,
    folderId: null,
    enabled: mayUpload && scope.projectId !== null,
  });
  if (!mayUpload || version === null) {
    return { action: null, inKnowledge: null };
  }
  if (scope.projectId === null) {
    return { action: "add" as const, inKnowledge: null };
  }
  const p = preview.data;
  const inKnowledge = p?.existing
    ? {
        version: p.existing.artefactVersion,
        href: `${projectHomePath(p.projectId, "files")}?document=${p.existing.documentId}`,
      }
    : null;
  const action =
    p?.state === "add"
      ? ("add" as const)
      : p?.state === "update" && p.existing?.canUpdate
        ? ("update" as const)
        : null;
  return { action, inKnowledge };
}

function KnowledgeButton(props: {
  ref: RefObject<HTMLButtonElement | null>;
  action: "add" | "update";
  version: number;
  title: string;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const update = props.action === "update";
  const Icon = update ? BookUp : BookPlus;
  return (
    <Button
      ref={props.ref}
      variant="outline"
      size="sm"
      onClick={props.onClick}
      aria-label={
        update
          ? t("artefactKnowledge.updateNamed", { title: props.title, version: props.version })
          : t("artefactKnowledge.addNamed", { title: props.title, version: props.version })
      }
    >
      <Icon aria-hidden="true" strokeWidth={1.75} />
      <span className="max-sm:sr-only">
        {update ? t("artefactKnowledge.update") : t("artefactKnowledge.add")}
      </span>
    </Button>
  );
}
